import { _js } from '@ifixit/localize';
import { ComponentProps, useCallback, useContext, useState } from 'react';
import { ApiContext } from '../Providers/apiContext';
import { getWorkOrder, getWorkOrderIncident } from './thdWorkOrder';
import { OrderContext } from '../Providers/orderContext';
import { trackAnalyticsCustomEvent, type CustomAnalyticsEvent } from '@ifixit/analytics';
import { MinimalDisclosure } from 'Guide/Schematics/Diagram/Logic/minimalDisclosure';
import RDModal from './RDModal';
import HDESModal from './HDESModal';
import { iFixitHotSpotItem } from '../Generic/iFixitHotSpotItem';
import { orangeTheme } from './colors';
import { cn } from '@ifixit/ui/lib/utils';

type ButtonProps = ComponentProps<'button'>;

function useDisclosure(options?: { defaultIsOpen?: boolean }) {
   const [isOpen, setIsOpen] = useState(options?.defaultIsOpen ?? false);
   const onOpen = useCallback(() => setIsOpen(true), []);
   const onClose = useCallback(() => setIsOpen(false), []);
   return { isOpen, onOpen, onClose };
}

const OrderPartApiButton = ({
   data,
   ...props
}: {
   data: iFixitHotSpotItem;
} & ButtonProps) => {
   const apiContext = useContext(ApiContext);
   const { isOpen, onOpen, onClose } = useDisclosure();

   if (apiContext.squad == 'Repair Depot' && apiContext.apiKey && getWorkOrder()) {
      return (
         <>
            <OrderRDPartApiButton data={data} onOpen={onOpen} {...props} />
            <RDModal isOpen={isOpen} onClose={onClose} />
         </>
      );
   } else if (
      apiContext.squad == 'HDES' &&
      apiContext.apiKey &&
      getWorkOrder() &&
      getWorkOrderIncident()
   ) {
      return (
         <>
            <OrderHDESPartApiButton data={data} onOpen={onOpen} {...props} />
            <HDESModal isOpen={isOpen} onClose={onClose} />
         </>
      );
   }

   return null;
};

const ApiButton = ({
   data,
   text,
   trackingData,
   onOpen,
   className,
   ...props
}: {
   data: iFixitHotSpotItem;
   text: string;
   trackingData: CustomAnalyticsEvent;
   onOpen: MinimalDisclosure['onOpen'];
} & ButtonProps) => {
   const orderContext = useContext(OrderContext);

   const onClick = () => {
      orderContext.setItem(data);
      // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      onOpen();
      trackAnalyticsCustomEvent(trackingData);
   };

   return (
      <button
         className={cn('text-white font-medium py-2 px-4 rounded-md', className)}
         style={{
            backgroundColor: orangeTheme.primaryButton,
         }}
         onMouseEnter={e =>
            (e.currentTarget.style.backgroundColor = orangeTheme.primaryButtonHover)
         }
         onMouseLeave={e => (e.currentTarget.style.backgroundColor = orangeTheme.primaryButton)}
         onClick={onClick}
         {...props}
      >
         {text}
      </button>
   );
};

const OrderRDPartApiButton = ({
   data,
   onOpen,
   ...props
}: {
   tabIndex?: number;
   data: iFixitHotSpotItem;
   onOpen: MinimalDisclosure['onOpen'];
} & ButtonProps) => {
   const wiki = data.wiki;
   const trackingData = {
      eventCategory: 'THDOrder',
      eventAction: 'Order',
      // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
      eventName: `Brand: ${wiki.brand} SKU: ${wiki.sku}`,
   };

   return (
      <ApiButton
         trackingData={trackingData}
         data={data}
         text={_js('Order Part')}
         onOpen={onOpen}
         {...props}
      />
   );
};

const OrderHDESPartApiButton = ({
   tabIndex,
   data,
   onOpen,
   ...props
}: {
   tabIndex?: number;
   data: iFixitHotSpotItem;
   onOpen: MinimalDisclosure['onOpen'];
} & ButtonProps) => {
   const wiki = data.wiki;
   const trackingData = {
      eventCategory: 'THDHDESOrder',
      eventAction: 'Order',
      // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
      eventName: `Brand: ${wiki.brand} SKU: ${wiki.sku}`,
   };

   return (
      <ApiButton
         trackingData={trackingData}
         tabIndex={tabIndex}
         data={data}
         text={_js('Add to Call')}
         onOpen={onOpen}
         {...props}
      />
   );
};

export { OrderPartApiButton };
